import { Button, Modal, Select, Space, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  apiAddListZaloAccountCompanies,
  apiDeleteListZaloAccountCompanies,
  apiGetListCompanies,
  apiGetListZaloAccountCompanies,
} from '../../api'

type Mapping = {
  id: string
  zaloAccountId: number
  cpCode: string
  assignedBy: number
  assignedAt: string
}

type Company = {
  id?: number
  code: string
  name?: string
}

type Props = {
  open: boolean
  account: any
  canAdd: boolean
  canDelete: boolean
  onClose: () => void
  onChanged?: () => void
}

export default function CompanyMappingModal({ open, account, canAdd, canDelete, onClose, onChanged }: Props) {
  const [mappings, setMappings] = useState<Mapping[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [picked, setPicked] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const accountId = Number(account?.id)

  const fetchMappings = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    try {
      const res: any = await apiGetListZaloAccountCompanies({ zaloAccountIds: [accountId], page: 1, size: 100 })
      setMappings(Array.isArray(res?.data?.zaloAccountCompanies) ? res.data.zaloAccountCompanies : [])
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách mapping thất bại')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const fetchCompanies = useCallback(async () => {
    try {
      const res: any = await apiGetListCompanies({ limit: 500 })
      const list = Array.isArray(res?.data?.companies) ? res.data.companies : []
      setCompanies(list)
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách công ty thất bại')
    }
  }, [])

  useEffect(() => {
    if (open) {
      void fetchMappings()
      void fetchCompanies()
      setPicked([])
    }
  }, [open, accountId, fetchMappings, fetchCompanies])

  const existingCpCodes = mappings.map((m) => m.cpCode)
  const addableOptions = companies
    .filter((c) => !!c.code && !existingCpCodes.includes(c.code))
    .map((c) => ({ value: c.code, label: c.name ? `${c.code} — ${c.name}` : c.code }))

  const handleAdd = async () => {
    if (picked.length === 0) return
    setSaving(true)
    try {
      const res: any = await apiAddListZaloAccountCompanies({
        zaloAccountCompanies: picked.map((cpCode) => ({ zaloAccountId: accountId, cpCode })),
      })
      const accepted = Array.isArray(res?.data?.zaloAccountCompanies) ? res.data.zaloAccountCompanies : []
      const rejected = Array.isArray(res?.data?.rejected) ? res.data.rejected : []
      if (rejected.length > 0) {
        message.warning(`${accepted.length} mapping đã gán, ${rejected.length} bị reject`)
      } else {
        message.success(`Đã gán ${accepted.length} đối tác`)
      }
      setPicked([])
      await fetchMappings()
      onChanged?.()
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Gán đối tác thất bại')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (mappingId: string) => {
    Modal.confirm({
      title: 'Bỏ gán đối tác',
      content: 'Xác nhận bỏ gán đối tác này khỏi Zalo account?',
      okText: 'Bỏ gán',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await apiDeleteListZaloAccountCompanies({ ids: [mappingId] })
          message.success('Đã bỏ gán')
          await fetchMappings()
          onChanged?.()
        } catch (err: any) {
          message.error(err?.message || 'Bỏ gán thất bại')
        }
      },
    })
  }

  return (
    <Modal
      title={`Đối tác phục vụ: ${account?.displayName || ''} (#${accountId})`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      width={700}
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ marginBottom: 8 }}>Tài khoản này có thể gửi thay cho các đối tác:</p>
        <Space wrap>
          {mappings.length === 0 ? (
            <span style={{ color: '#999' }}>Chưa gán đối tác nào</span>
          ) : (
            mappings.map((m) => (
              <Tag
                key={m.id}
                closable={canDelete}
                onClose={(e) => { e.preventDefault(); handleRemove(m.id) }}
                color="blue"
              >
                {m.cpCode}
              </Tag>
            ))
          )}
        </Space>
      </div>

      {canAdd && (
        <div>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>Thêm đối tác</p>
          <Space style={{ width: '100%' }}>
            <Select
              mode="multiple"
              showSearch
              placeholder="Chọn đối tác từ danh sách..."
              style={{ minWidth: 360, flex: 1 }}
              value={picked}
              onChange={(v) => setPicked(v)}
              options={addableOptions}
              filterOption={(input, option) =>
                String(option?.label || '').toLowerCase().includes(input.toLowerCase())
              }
              loading={loading}
            />
            <Button type="primary" onClick={handleAdd} loading={saving} disabled={picked.length === 0}>
              Gán
            </Button>
          </Space>
        </div>
      )}
    </Modal>
  )
}
